import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TransactionService } from '@/services/TransactionService';
import { budgetKeys } from './useBudgets';
import type { 
  Transaction, 
  CreateTransactionRequest, 
  UpdateTransactionRequest 
} from '@/types';

// Query keys
export const transactionKeys = {
  all: ['transactions'] as const,
  byBudget: (budgetId: string) => [...transactionKeys.all, 'budget', budgetId] as const,
  byBudgetTree: (budgetId: string, includeDescendants: boolean) =>
    [...transactionKeys.all, 'tree', budgetId, includeDescendants] as const,
  detail: (id: string) => [...transactionKeys.all, 'detail', id] as const,
  byBudgetList: (ids: string[]) => [...transactionKeys.all, 'list', ...ids] as const,
  byDateRange: (budgetId: string, start: string, end: string) => 
    [...transactionKeys.all, 'budget', budgetId, 'range', start, end] as const,
};

/**
 * Hook for fetching transactions for a budget.
 */
export function useTransactions(budgetId: string | undefined) {
  return useQuery({
    queryKey: transactionKeys.byBudget(budgetId ?? ''),
    queryFn: () => TransactionService.getByBudget(budgetId!),
    enabled: !!budgetId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching transactions for a budget tree in a single request.
 */
export function useTransactionsForBudgetTree(
  budgetId: string | undefined,
  includeDescendants: boolean
) {
  return useQuery({
    queryKey: transactionKeys.byBudgetTree(budgetId ?? '', includeDescendants),
    queryFn: () => TransactionService.getByBudgetTree(budgetId!, includeDescendants),
    enabled: !!budgetId,
    staleTime: 30 * 1000,
  });
}

/**
 * @deprecated Prefer useTransactionsForBudgetTree for hierarchical budgets.
 */
export function useTransactionsForBudgets(budgetIds: string[]) {
  const ids = Array.from(new Set(budgetIds)).filter(Boolean);

  return useQuery({
    queryKey: transactionKeys.byBudgetList(ids),
    queryFn: async () => {
      const results = await Promise.all(ids.map((id) => TransactionService.getByBudget(id)));
      return results.flat();
    },
    enabled: ids.length > 0,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching a single transaction.
 */
export function useTransaction(id: string | undefined) {
  return useQuery({
    queryKey: transactionKeys.detail(id ?? ''),
    queryFn: () => TransactionService.getById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching transactions by date range.
 */
export function useTransactionsByDateRange(
  budgetId: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined
) {
  return useQuery({
    queryKey: transactionKeys.byDateRange(budgetId ?? '', startDate ?? '', endDate ?? ''),
    queryFn: () => TransactionService.getByDateRange(budgetId!, startDate!, endDate!),
    enabled: !!budgetId && !!startDate && !!endDate,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for creating a transaction.
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTransactionRequest) => TransactionService.create(request),
    onSuccess: async (transaction) => {
      // Get the budget to find its parent
      const budget = queryClient.getQueryData<any>(budgetKeys.detail(transaction.budgetId));
      
      queryClient.invalidateQueries({ 
        queryKey: transactionKeys.byBudget(transaction.budgetId) 
      });
      // Invalidate transactions list for all budget descendants
      queryClient.invalidateQueries({ 
        queryKey: transactionKeys.all 
      });
      // Also invalidate budget to update spent amounts
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.activeSummaries() });
      // Invalidate specific budget detail
      queryClient.invalidateQueries({ 
        queryKey: budgetKeys.detail(transaction.budgetId) 
      });
      // Invalidate parent budget if exists (to update aggregated spent amounts)
      if (budget?.parentId) {
        queryClient.invalidateQueries({ 
          queryKey: budgetKeys.detail(budget.parentId) 
        });
      }
    },
  });
}

/**
 * Hook for updating a transaction.
 * Pass `originalBudgetId` whenever the request may change the transaction's budget
 * so the old budget's cache is also invalidated.
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: UpdateTransactionRequest;
      /** Budget the transaction currently belongs to (before the update). */
      originalBudgetId?: string;
    }) => TransactionService.update(id, request),
    onSuccess: async (transaction, variables) => {
      const newBudgetId = transaction.budgetId;
      const oldBudgetId = variables.originalBudgetId ?? newBudgetId;
      const budgetChanged = oldBudgetId !== newBudgetId;

      // Prefer response tags; if the update payload omits them, keep cached tags.
      const previous = queryClient.getQueryData<Transaction>(transactionKeys.detail(transaction.id));
      const merged =
        transaction.tags != null
          ? transaction
          : previous?.tags
            ? { ...transaction, tags: previous.tags }
            : transaction;
      queryClient.setQueryData(transactionKeys.detail(transaction.id), merged);

      // Invalidate transaction lists for both old and new budget
      queryClient.invalidateQueries({ queryKey: transactionKeys.byBudget(newBudgetId) });
      if (budgetChanged) {
        queryClient.invalidateQueries({ queryKey: transactionKeys.byBudget(oldBudgetId) });
      }

      // Invalidate all aggregated transaction queries
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });

      // Invalidate budget summaries (spent/remaining) for affected budgets
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.activeSummaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(newBudgetId) });
      if (budgetChanged) {
        queryClient.invalidateQueries({ queryKey: budgetKeys.detail(oldBudgetId) });
      }

      // Invalidate parent budgets so their aggregated amounts update too
      const newBudget = queryClient.getQueryData<any>(budgetKeys.detail(newBudgetId));
      if (newBudget?.parentId) {
        queryClient.invalidateQueries({ queryKey: budgetKeys.detail(newBudget.parentId) });
      }
      if (budgetChanged) {
        const oldBudget = queryClient.getQueryData<any>(budgetKeys.detail(oldBudgetId));
        if (oldBudget?.parentId) {
          queryClient.invalidateQueries({ queryKey: budgetKeys.detail(oldBudget.parentId) });
        }
      }
    },
  });
}

/**
 * Hook for deleting a transaction.
 */
export function useDeleteTransaction(budgetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => TransactionService.delete(id),
    onSuccess: async (_, id) => {
      // Get the budget to find its parent
      const budget = queryClient.getQueryData<any>(budgetKeys.detail(budgetId));
      
      queryClient.invalidateQueries({ 
        queryKey: transactionKeys.byBudget(budgetId) 
      });
      queryClient.removeQueries({ queryKey: transactionKeys.detail(id) });
      // Invalidate transactions list for all budget descendants
      queryClient.invalidateQueries({ 
        queryKey: transactionKeys.all 
      });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.activeSummaries() });
      // Invalidate specific budget detail
      queryClient.invalidateQueries({ 
        queryKey: budgetKeys.detail(budgetId) 
      });
      // Invalidate parent budget if exists
      if (budget?.parentId) {
        queryClient.invalidateQueries({ 
          queryKey: budgetKeys.detail(budget.parentId) 
        });
      }
    },
  });
}

/**
 * Hook for transaction utilities.
 */
export function useTransactionUtils() {
  return {
    validate: TransactionService.validate,
    isHistorical: TransactionService.isHistorical,
    sumTransactions: TransactionService.sumTransactions,
    groupByDate: TransactionService.groupByDate,
    groupByCategory: TransactionService.groupByCategory,
  };
}

/**
 * Hook for optimistic transaction updates.
 */
export function useOptimisticTransactionUpdate(budgetId: string) {
  const queryClient = useQueryClient();

  const setOptimistic = (transaction: Transaction) => {
    queryClient.setQueryData(transactionKeys.detail(transaction.id), transaction);
    
    queryClient.setQueryData<Transaction[]>(
      transactionKeys.byBudget(budgetId),
      (old) => {
        if (!old) return [transaction];
        const index = old.findIndex((t) => t.id === transaction.id);
        if (index === -1) return [...old, transaction];
        const newData = [...old];
        newData[index] = transaction;
        return newData;
      }
    );
  };

  const removeOptimistic = (id: string) => {
    queryClient.setQueryData<Transaction[]>(
      transactionKeys.byBudget(budgetId),
      (old) => old?.filter((t) => t.id !== id) ?? []
    );
  };

  const rollback = (id: string, previousTransaction: Transaction | undefined) => {
    if (previousTransaction) {
      queryClient.setQueryData(transactionKeys.detail(id), previousTransaction);
    } else {
      queryClient.removeQueries({ queryKey: transactionKeys.detail(id) });
    }
    queryClient.invalidateQueries({ queryKey: transactionKeys.byBudget(budgetId) });
  };

  return { setOptimistic, removeOptimistic, rollback };
}

/**
 * Hook for moving a transaction to a different budget.
 * Thin wrapper around useUpdateTransaction that pre-fills the move fields.
 */
export function useMoveTransaction() {
  const updateTransaction = useUpdateTransaction();

  const moveAsync = (transaction: Transaction, targetBudgetId: string) =>
    updateTransaction.mutateAsync({
      id: transaction.id,
      originalBudgetId: transaction.budgetId,
      request: {
        budgetId: targetBudgetId,
        amount: transaction.amount,
        currency: transaction.currency,
        date: transaction.date,
        note: transaction.note ?? undefined,
        merchant: transaction.merchant ?? undefined,
        category: transaction.category ?? undefined,
        version: transaction.version ?? 0,
      },
    });

  return {
    moveAsync,
    isPending: updateTransaction.isPending,
  };
}

/**
 * Hook for grouped transaction display.
 */
export function useGroupedTransactions(budgetId: string | undefined) {
  const { data: transactions = [], ...rest } = useTransactions(budgetId);
  const utils = useTransactionUtils();

  const byDate = utils.groupByDate(transactions);
  const byCategory = utils.groupByCategory(transactions);
  const total = utils.sumTransactions(transactions);

  const sortedDates = Array.from(byDate.keys()).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  return {
    transactions,
    byDate,
    byCategory,
    sortedDates,
    total,
    ...rest,
  };
}
